import React from "react";
import { useTranslation } from "react-i18next";
import { IconCredit } from "icons";
import { cn } from "utils/cn";

interface CreditShortfallInfoProps {
  requiredCredits: number | null;
  remainingCredits: number | null;
  tone?: "light" | "dark";
}

// 402 credit-gate shortfall numbers (REF-5453). On dual-bucket gates (/start_tryon,
// /start_photoshooting) the BE sends the MAIN pool values only (REF-5398) — render the raw
// numbers, never "you need X more" math derived from required - remaining.
const CreditShortfallInfo: React.FC<CreditShortfallInfoProps> = ({ requiredCredits, remainingCredits, tone = "light" }) => {
  const { t } = useTranslation();

  if (requiredCredits === null && remainingCredits === null) return null;

  const valueClass = tone === "dark" ? "text-white" : "dark:text-white";

  return (
    <div className={cn("flex w-full items-stretch flex-shrink-0 rounded-lg p-4", tone === "dark" ? "bg-white/5" : "bg-grey-light/30 dark:bg-black-300/50")}>
      {requiredCredits !== null && (
        <div className="flex flex-1 flex-col items-center gap-1">
          <span className="text-bodySm text-grey text-center">{t("billing:credit_shortfall.required")}</span>
          <span className={cn("flex items-center gap-1 text-h5", valueClass)}>
            <IconCredit className="w-4 h-4 flex-shrink-0" />
            {requiredCredits}
          </span>
        </div>
      )}
      {requiredCredits !== null && remainingCredits !== null && <div className={cn("w-px self-stretch", tone === "dark" ? "bg-white/10" : "bg-grey-light dark:bg-black-300")} />}
      {remainingCredits !== null && (
        <div className="flex flex-1 flex-col items-center gap-1">
          <span className="text-bodySm text-grey text-center">{t("billing:credit_shortfall.remaining")}</span>
          <span className={cn("flex items-center gap-1 text-h5", valueClass)}>
            <IconCredit className="w-4 h-4 flex-shrink-0" />
            {remainingCredits}
          </span>
        </div>
      )}
    </div>
  );
};

export default CreditShortfallInfo;
